import styled from 'styled-components';
import { ColorBeauty2, ColorClubMatas1, ColorClubMatasPlus1 } from 'autogen/design-tokens/tokens';
import type { MenuItemMyProfileViewModel } from 'autogen/swagger/all';
import useDrawer from 'components/generic/Drawer/hooks/useDrawer';
import Icon from 'components/Icon';
import ListItem from 'components/List/components/ListItem/ListItem';
import LinkAction from 'components/shared/Routing/components/LinkAction';
import MenuWithHeader from 'components/sideMenu/components/Menu/components/MenuWithHeader';
import format from 'helpers/translations';
import useIsClubMatasMember from 'hooks/user/useIsClubMatasMember';
import { GA4Tracking } from 'modules/tracking/GA4';
import type { DefaultMenuItemProps } from '../types';
import DefaultMenuItem from './DefaultMenuItem';

type MyProfileMenuItemProps = {
    model: MenuItemMyProfileViewModel;
    defaultProps: DefaultMenuItemProps;
};

const Wrapper = styled.div`
    display: flex;
    justify-content: space-between;
`;

const ClubMatasColor = styled.span<{ isClubMatasPlus: boolean }>`
    font-weight: bold;
    color: ${({ isClubMatasPlus }) => (isClubMatasPlus ? ColorClubMatasPlus1 : ColorClubMatas1)};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: right;
    max-width: 112px;
    line-height: normal;
`;

const Text = styled.span`
    white-space: nowrap;
`;

const StyledLinkAction = styled(LinkAction)`
    display: block;
    width: 100%;

    &, &:hover, &:focus {
        text-decoration: none;
    }
`;

export default function MyProfileMenuItem({ model, defaultProps, ...restProps }: MyProfileMenuItemProps) {
    const { text, subText, isSelected, linkAction, target, isExpanded, isChecked, subMenu } = model;
    const { iconUrl, styling, id } = defaultProps;
    const { isMatasPlusMember } = useIsClubMatasMember();

    if (subMenu.length) {
        return <MyProfileSubMenuItem model={model} defaultProps={defaultProps} />;
    }

    const checkmark = !isExpanded && isChecked && isSelected && <Icon type="Checkmark" size={16} fill={ColorBeauty2} />;

    function tracking() {
        GA4Tracking({
            eventName: 'link_click',
            context: 'my_matas',
            category: 'navigation',
            eventParams: [
                { param_name: 'link_type', param_value: 'internal' },
                { param_name: 'list_name', param_value: text },
                { param_name: 'id', param_value: id }
            ]
        });
    }

    return (
        <DefaultMenuItem.Anchor
            anchorProps={{ action: linkAction, target }}
            selected={isSelected}
            styling={styling}
            iconUrl={iconUrl}
            BeforeRenderer={() => checkmark || undefined}
            onClick={tracking}
            {...restProps}
        >
            <Wrapper>
                <Text> {isSelected ? <b>{text}</b> : text}</Text>
                <ClubMatasColor isClubMatasPlus={isMatasPlusMember}> {format(subText?.text)}</ClubMatasColor>
            </Wrapper>
        </DefaultMenuItem.Anchor>
    );
}

function MyProfileSubMenuItem({
    model: { text, isSelected, linkAction, subMenu },
    defaultProps: { iconUrl, styling, id },
    ...restProps
}: MyProfileMenuItemProps) {
    const smartSideDrawerId = `${subMenu[0].id}-myprofile-menu`;
    const { open: openSubMenu, close: closeSubMenu } = useDrawer(smartSideDrawerId);

    return (
        <>
            {linkAction ? (
                <ListItem
                    AfterRenderer={() => <Icon type="ChevronRight" size={16} onClick={openSubMenu} {...restProps} />}
                    selected={isSelected}
                >
                    <StyledLinkAction action={linkAction}>{text}</StyledLinkAction>
                </ListItem>
            ) : (
                <DefaultMenuItem.Button
                    buttonProps={{ onClick: openSubMenu }}
                    AfterRenderer={() => <Icon type="ChevronRight" size={16} />}
                    selected={isSelected}
                    styling={styling}
                    iconUrl={iconUrl}
                    {...restProps}
                >
                    {text}
                </DefaultMenuItem.Button>
            )}

            <MenuWithHeader
                menu={subMenu[0]}
                drawerId={smartSideDrawerId}
                backButtonCallback={closeSubMenu}
                animate={false}
            />
        </>
    );
}
